// frontend/src/components/FraslexikonKort/FraslexikonKort.js
/**
 * Självständigt dashboardkort för fraslexikonet (Spår 10).
 * Visar antal fraser, snabbsök (klick kopierar frasens text) och knappar
 * för att hantera/importera. Öppnar PhraseManager som dialog.
 *
 * Placeras på dashboarden (LandingPage) av integratören efter Spår 8:
 *   import FraslexikonKort from '../FraslexikonKort';
 *   <FraslexikonKort />
 */
import React, { useCallback, useEffect, useState } from 'react';
import { getPhrases, PHRASES_CHANGED_EVENT } from '../JournalEditor/api/journalApi';
import PhraseManager from '../PhraseManager';
import './FraslexikonKort.css';

const MAX_RESULTS = 5;

function FraslexikonKort({ className = '' }) {
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [managerView, setManagerView] = useState(null); // null | 'list' | 'import'
  const [copiedId, setCopiedId] = useState(null);

  const loadPhrases = useCallback(async () => {
    try {
      setError(false);
      const data = await getPhrases();
      setPhrases(data.personal_phrases || []);
    } catch (err) {
      console.error('Kunde inte ladda fraslexikon:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPhrases();
    window.addEventListener(PHRASES_CHANGED_EVENT, loadPhrases);
    return () => window.removeEventListener(PHRASES_CHANGED_EVENT, loadPhrases);
  }, [loadPhrases]);

  const q = query.trim().toLowerCase();
  const matches = q
    ? phrases
        .filter(p => p.title.toLowerCase().includes(q) || p.phrase_text.toLowerCase().includes(q))
        .sort((a, b) => {
          // Triggerord som börjar på sökordet först
          const aStarts = a.title.toLowerCase().startsWith(q) ? 0 : 1;
          const bStarts = b.title.toLowerCase().startsWith(q) ? 0 : 1;
          return aStarts - bStarts || a.title.localeCompare(b.title, 'sv');
        })
    : [];

  const copyPhrase = async (phrase) => {
    try {
      await navigator.clipboard.writeText(phrase.phrase_text);
      setCopiedId(phrase.phrase_id);
      setTimeout(() => setCopiedId(id => (id === phrase.phrase_id ? null : id)), 1500);
    } catch (err) {
      console.error('Kunde inte kopiera frasen:', err);
    }
  };

  const count = phrases.length;

  return (
    <section className={`fraslexikon-kort ${className}`} aria-labelledby="fraslexikon-kort-titel">
      <div className="fraslexikon-kort-header">
        <h3 id="fraslexikon-kort-titel">Fraslexikon</h3>
        <span className="fraslexikon-kort-count">
          {loading ? '…' : `${count} ${count === 1 ? 'fras' : 'fraser'}`}
        </span>
      </div>

      {error ? (
        <p className="fraslexikon-kort-hint">
          Kunde inte ladda fraserna.{' '}
          <button type="button" className="fraslexikon-kort-link" onClick={loadPhrases}>
            Försök igen
          </button>
        </p>
      ) : !loading && count === 0 ? (
        <p className="fraslexikon-kort-hint">
          Spara återkommande texter och infoga dem med <kbd>;</kbd> + triggerord.
          Importera ditt lexikon från TakeCare eller skapa egna fraser.
        </p>
      ) : (
        <>
          <div className="fraslexikon-kort-search">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="search"
              placeholder="Sök fras..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              aria-label="Sök i fraslexikon"
            />
          </div>
          {q && (
            <ul className="fraslexikon-kort-results">
              {matches.length === 0 && <li className="fraslexikon-kort-empty">Ingen fras matchar</li>}
              {matches.slice(0, MAX_RESULTS).map(p => (
                <li key={p.phrase_id}>
                  <button type="button" onClick={() => copyPhrase(p)} title="Klicka för att kopiera texten">
                    <code>;{p.title}</code>
                    <span className="fraslexikon-kort-preview">{p.phrase_text}</span>
                    <span className="fraslexikon-kort-copy">
                      {copiedId === p.phrase_id ? 'Kopierad' : 'Kopiera'}
                    </span>
                  </button>
                </li>
              ))}
              {matches.length > MAX_RESULTS && (
                <li className="fraslexikon-kort-more">
                  +{matches.length - MAX_RESULTS} till,{' '}
                  <button type="button" className="fraslexikon-kort-link" onClick={() => setManagerView('list')}>
                    visa alla
                  </button>
                </li>
              )}
            </ul>
          )}
          {!q && (
            <p className="fraslexikon-kort-hint">
              Skriv <kbd>;</kbd> + triggerord i en journaltext för att infoga en fras.
            </p>
          )}
        </>
      )}

      <div className="fraslexikon-kort-actions">
        <button type="button" className="fraslexikon-kort-btn primary" onClick={() => setManagerView('list')}>
          Hantera
        </button>
        <button type="button" className="fraslexikon-kort-btn" onClick={() => setManagerView('import')}>
          Importera
        </button>
      </div>

      {managerView && (
        <PhraseManager initialView={managerView} onClose={() => setManagerView(null)} />
      )}
    </section>
  );
}

export default FraslexikonKort;
